import React, { useState, useEffect, useRef } from "react";
import { EyeOutlined } from "@ant-design/icons";
import {
  Table, Select, Tooltip,
} from "antd";
import { toast } from "react-toastify";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { fetchQCData, fetchQCAnalystUsers, markManualQcAssignTo } from "../../../actions/qc";
import { getSearchOptions } from "../../../actions/batchReports";
import { fetchTestCategory } from "../../../actions/chemistry";
import QCFilterBar, { defaultDateRange } from "./QCFilterBar";
import MarkSampleQCModal from "./MarkSampleQCModal/components/MarkSampleQCModal";
import useAbortController from "../../Common/hooks/useAbortController";
import { formatAntdDateRange, getUser } from "../../../utils/helpers";
import { TEST_OVERDUE_FLAGS, getTestOverdueFlag, MANUAL_QC_FLAGS } from "../../../utils/statusConfig";
import "./QC.css";
import WoDetailsModal from "../BatchReports/WoDetailsModal";
import StatusTag from "../../Common/components/StatusTag";

/**
 * Normalize QC dashboard API item into the flat shape expected by the table/UI.
 * Supports both the old flat format and the new nested format shown in the example payload.
 */
const normalizeManualQcItem = (item) => {
  if (!item || typeof item !== "object") {
    return {};
  }

  const {
    workorder, customer, sample, on_dashboard_since,
  } = item;
  const test = sample.test;
  const qc = test.qc;
  const ml = qc.ml;
  const software = qc.software;
  const manual = qc.manual;

  return {
    // Workorder
    workorder_id: workorder.workorder_id,
    workorder_id_display: workorder.workorder_id_display,
    wo_id_pk: workorder.wo_id_pk,
    wo_id_pk_display: workorder.wo_id_pk_display,
    related_workorders: workorder.related_workorders,

    // Customer
    customer_id: customer.customer_id,
    customer_id_display: customer.customer_id_display,
    customer_id_pk: customer.customer_id_pk,
    company_name: customer.company_name,

    // Sample
    genista_id: sample.genista_id,
    sample_id: sample.sample_id,
    sample_type: sample.type,
    overdue_by: sample.overdue_by_days,
    on_dashboard_since,

    // Test
    test_id_pk: test.test_id_pk,
    tis_pk: test.tis_pk,
    analysis: test.analysis,
    test_category: test.test_category,
    test_status: test.test_status,
    result: test.result,
    test_default_value: test.test_default_value,
    is_retest: workorder.is_retest,

    // QC - ML
    ml_qc_result: ml.ml_qc_result,
    ml_qc_status: ml.ml_qc_status,
    ml_qc_result_timestamp: ml.ml_qc_result_timestamp,
    ml_qc_correction: ml.ml_qc_correction,
    ml_qc_comments: ml.ml_qc_comments,
    ml_correct_by: ml.ml_correct_by,
    ml_correct_at: ml.ml_correct_at,
    ml_soft_qc_result: ml.ml_soft_qc_result,
    ml_soft_qc_status: ml.ml_soft_qc_status,
    ml_soft_qc_timestamp: ml.ml_soft_qc_timestamp,

    // QC - Software
    qc_result: software.qc_result,
    qc_status: software.qc_status,
    qc_result_timestamp: software.qc_result_timestamp,

    // QC - Manual
    manual_qc_result: manual.manual_qc_result,
    manual_qc_status: manual.manual_qc_status,
    manual_qc_by: manual.manual_qc_by,
    manual_qc_at: manual.manual_qc_at,
    manual_qc_assigned_to: manual.manual_qc_assigned_to,
    manual_qc_assigned_to_name: manual.manual_qc_assigned_to_name,
  };
};

const columns = [
  {
    title: "Customer ID",
    dataIndex: "customer_id_display",
    key: "customer_id_display",
  },
  {
    title: "WO",
    dataIndex: "workorder_id",
    key: "workorder_id",
  },
  {
    title: "Genista ID",
    dataIndex: "genista_id",
    key: "genista_id",
  },
  // {
  //   title: "Sample ID",
  //   dataIndex: "sample_id",
  //   key: "sample_id",
  // },
  // {
  //   title: "Sample Type",
  //   dataIndex: "sample_type",
  //   key: "sample_type",
  // },
  {
    title: "Test",
    dataIndex: "analysis",
    key: "analysis",
  },
  {
    title: "Test Category",
    dataIndex: "test_category",
    key: "testingCategory",
  },
  {
    title: "Result",
    dataIndex: "result",
    key: "result",
  },
  {
    title: "Manual QC",
    dataIndex: "manual_qc_result",
    key: "manual_qc_result",
  },
  // {
  //   title: "Spec",
  //   dataIndex: "test_default_value",
  //   key: "test_default_value",
  // },
  {
    title: "Assign To",
    dataIndex: "manual_qc_assigned_to_name",
    key: "manual_qc_assigned_to_name",
    width: 180,
  },
  {
    title: "Pending For",
    dataIndex: "ml_soft_qc_timestamp",
    key: "ml_soft_qc_timestamp",
  },
  // {
  //   title: (
  //     <Flex justify="flex-start" align="center" gap={8}>
  //       <span>Due Status</span>
  //       <Tooltip
  //         className="QCTable__Tooltip"
  //         overlayClassName="QCTable__TooltipOverlay"
  //         placement="topRight"
  //         title={(
  //           <div>
  //             <div className="QCTable__TooltipTitle">
  //               Due Status
  //             </div>
  //             <div className="QCTable__TooltipContent">
  //               <div className="QCTable__TooltipItem">
  //                 <span className="QCTable__TooltipBullet">•</span>
  //                 <span className="QCTable__TooltipStatus--dueSoon">
  //                   Due soon
  //                 </span>
  //                 <span className="QCTable__TooltipDescription">
  //                   - Test is due soon
  //                 </span>
  //               </div>
  //               <div className="QCTable__TooltipItem">
  //                 <span className="QCTable__TooltipBullet">•</span>
  //                 <span style={TEST_OVERDUE_FLAGS["Due today"].textStyle}>
  //                   Due today
  //                 </span>
  //                 <span className="QCTable__TooltipDescription">
  //                   - Test is due today
  //                 </span>
  //               </div>
  //               <div className="QCTable__TooltipItem">
  //                 <span className="QCTable__TooltipBullet">•</span>
  //                 <span style={TEST_OVERDUE_FLAGS["Due yesterday"].textStyle}>
  //                   Due yesterday
  //                 </span>
  //                 <span className="QCTable__TooltipDescription">
  //                   - Test was due 1 day ago
  //                 </span>
  //               </div>
  //               <div className="QCTable__TooltipItem">
  //                 <span className="QCTable__TooltipBullet">•</span>
  //                 <span style={TEST_OVERDUE_FLAGS.Overdue.textStyle}>
  //                   Overdue
  //                 </span>
  //                 <span className="QCTable__TooltipDescription">
  //                   - Test is overdue by 2 or more days
  //                 </span>
  //               </div>
  //             </div>
  //           </div>
  //         )}
  //       >
  //         <InfoCircleOutlined className="QCTable__InfoIcon" />
  //       </Tooltip>
  //     </Flex>
  //   ),
  //   dataIndex: "overdue_by",
  //   key: "overdue_status",
  // },

];

const QCTableRow = ({
  record,
  fetchQcApi,
  readOnly = false,
  onRowClick,
  ...antdProps
}) => {
  const [openModal, setOpenModal] = useState(false);

  const onCancelModal = (refresh) => {
    if (refresh) {
      fetchQcApi();
    }
    setOpenModal(false);
  };
  if (!record) {
    return <tr {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }

  if (readOnly) {
    return (
      <tr
        {...antdProps} // eslint-disable-line
        onClick={() => onRowClick && onRowClick(record)}
        className="QCTableRow"
      />
    );
  }

  return (
    <>
      <tr
        {...antdProps} // eslint-disable-line
        onClick={() => setOpenModal(true)}
        className="QCTableRow"
      />
      {openModal && (
        <MarkSampleQCModal
          open
          rowData={record}
          onCancel={onCancelModal}
        />
      )}
    </>
  );
};

const QCTableCell = ({
  record,
  dataIndex,
  handlePasteSearchBy,
  assignToList: ogAssignToList,
  assignToLoading,
  handleUpdateAssignTo,
  openWoDetailsModal,
  readOnly = false,
  isHistory = false,
  ...antdProps
}) => {
  let assignToList = [];
  const user = getUser();
  const [assignTo, setAssignTo] = useState(record && record.manual_qc_assigned_to);
  if (ogAssignToList && ogAssignToList.length > 0) {
    assignToList = ogAssignToList.map((item) => ({ ...item })).filter((item) => item.value !== "");
  }
  const onCustomCellClicked = (e, searchValue) => {
    e.stopPropagation();
    handlePasteSearchBy(searchValue);
  };

  if (!record) {
    return <td {...antdProps} />; // eslint-disable-line react/jsx-props-no-spreading
  }
  let childNode = record[dataIndex];

  if (dataIndex === "manual_qc_assigned_to_name") {
    childNode = record.manual_qc_assigned_to_name;
  }

  if (dataIndex === "manual_qc_result") {
    const statusKey = record[dataIndex];
    const flag = MANUAL_QC_FLAGS[statusKey];
    const updatedBy = record.manual_qc_by;
    const updatedAt = record.manual_qc_at === "" ? "" : moment(record.manual_qc_at).format("MM/DD/YYYY, h:mm:ss a");
    const isRetest = record.is_retest === true;

    const tooltipTitle = (
      <span>
        Updated By:
        {" "}
        {updatedBy}
        <br />
        Updated At:
        {" "}
        {updatedAt}
      </span>
    );

    childNode = (
      <div className="QCTable__ManualQCCell">
        <Tooltip placement="top" title={tooltipTitle}>
          <span>
            <StatusTag
              result={flag.value}
              type="MANUAL_QC"
            />
          </span>
        </Tooltip>
        {!isHistory && String(statusKey) === "0" && isRetest && (
          <div className="QCTable__ManualQCText">
            Retest Result Available
          </div>
        )}
      </div>
    );
  }

  if (dataIndex === "workorder_id") {
    childNode = (
      <div className="QCTable__CustomCell">
        <div className="QCTable__WOCell">
          <span className="QCTable__WOCell__Text" onClick={(e) => onCustomCellClicked(e, record[dataIndex])}>{record[dataIndex]}</span>
          <EyeOutlined
            className="QCTable__WOCell__Icon"
            onClick={(e) => {
              e.stopPropagation();
              openWoDetailsModal(record.wo_id_pk);
            }}
          />
        </div>
        {!isHistory && (
          <div className="QCTable__RelatedWOText">
            {record.related_workorders.join(", ")}
          </div>
        )}
      </div>
    );
  }

  if (dataIndex === "genista_id") {
    childNode = (
      <div className="QCTable__CustomCell QCTable__WOCell">
        <Tooltip placement="top" title={record.sample_id}>
          <span
            className="QCTable__WOCell__Text"
            onClick={(e) => onCustomCellClicked(e, record[dataIndex])}
          >
            {record[dataIndex]}
          </span>
        </Tooltip>
        <EyeOutlined
          className="QCTable__WOCell__Icon"
          onClick={(e) => {
            e.stopPropagation();
            openWoDetailsModal(record.wo_id_pk, record.genista_id);
          }}
        />
      </div>
    );
  }

  if (dataIndex === "manual_qc_at" || dataIndex === "ml_soft_qc_timestamp") {
    const parsedTime = moment(record[dataIndex]);
    const time = parsedTime.isValid() ? parsedTime.format("MM/DD/YYYY, h:mm:ss a") : "";

    const onDashboardSince = record.on_dashboard_since;

    if (dataIndex === "ml_soft_qc_timestamp") {
      const displayValue = onDashboardSince !== "" ? onDashboardSince : "-";
      childNode = (
        <Tooltip placement="top" title={time}>
          <span>{displayValue}</span>
        </Tooltip>
      );
    } else {
      childNode = time;
    }
  }

  const assignToOnSelect = async (v) => {
    setAssignTo(v);
    const updateSuccess = await handleUpdateAssignTo(v, record);
    if (!updateSuccess) {
      setAssignTo(record.manual_qc_assigned_to);
    }
  };

  if (!readOnly && dataIndex === "manual_qc_assigned_to_name" && user.permissionMap.edit_qc_assign_to) {
    const assigneeFromApi = record.manual_qc_assigned_to != null && record.manual_qc_assigned_to_name
      ? { value: record.manual_qc_assigned_to, label: record.manual_qc_assigned_to_name }
      : null;
    const optionsForSelect = assigneeFromApi
      ? [assigneeFromApi, ...assignToList.filter((o) => String(o.value) !== String(record.manual_qc_assigned_to))]
      : assignToList;

    childNode = (
      <Select
        showSearch
        className="QCTable__AssignTo"
        popupClassName="QCTable__AssignTo__Menu"
        loading={assignToLoading}
        options={optionsForSelect}
        value={assignTo}
        onSelect={assignToOnSelect}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        filterOption={(inputValue, option) => option.label.toLowerCase().includes(inputValue.toLowerCase())}
        listHeight={400}
      />
    );
  }

  if (dataIndex === "overdue_by") {
    const overdueBy = record[dataIndex];
    const displayableDueFlags = ["Due soon", "Due today", "Due yesterday", "Overdue"];
    if (overdueBy !== undefined && overdueBy !== null) {
      const overdueFlag = getTestOverdueFlag(parseInt(overdueBy));
      if (displayableDueFlags.includes(overdueFlag)) {
        const overdueFlagConfig = TEST_OVERDUE_FLAGS[overdueFlag];
        const overdueLabel = overdueFlagConfig.label;

        // Format days display (context-aware)
        let daysDisplay = "";
        if (overdueBy < 0) {
          const days = Math.abs(overdueBy);
          daysDisplay = days === 1 ? "(In 1 day)" : `(In ${days} days)`;
        } else if (overdueBy === 0) {
          daysDisplay = "(Today)";
        } else if (overdueBy === 1) {
          daysDisplay = "(1 day Ago)";
        } else {
          daysDisplay = `(${overdueBy} days Ago)`;
        }

        childNode = (
          <div
            className="QCTable__DueStatusCell"
            style={overdueFlagConfig.textStyle}
          >
            <span className="QCTable__DueStatusLabel">
              {overdueLabel}
            </span>
            <span className="QCTable__DueStatusDays">
              {daysDisplay}
            </span>
          </div>
        );
      } else {
        childNode = "-";
      }
    } else {
      childNode = "-";
    }
  }

  return (<td {...antdProps}>{childNode}</td>)// eslint-disable-line
};

const QC = ({ readOnly = false, onRowClick, source }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState(defaultDateRange());
  const [selectedCategory, setSelectedCategory] = useState("");
  const defaultCustomerOption = { value: "", label: "All Customers" };
  const [customerList, setCustomerList] = useState([defaultCustomerOption]);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [customerLoading, setCustomerLoading] = useState(true);
  const defaultCategoryOption = { value: "", label: "All Categories" };
  const [testCategories, setTestCategories] = useState([defaultCategoryOption]);
  // const [selectedMLSoftQC, setSelectedMLSoftQC] = useState("");
  // const [selectedAIQC, setSelectedAIQC] = useState("");
  // const [selectedSoftwareQC, setSelectedSoftwareQC] = useState("");
  const isHistory = source === "QC";
  const manualQcDefaultValue = isHistory ? "" : 0;
  const [selectedManualQC, setSelectedManualQC] = useState(manualQcDefaultValue);
  const defaultAssignTo = { value: "", label: "All Assign To" };
  const [selectedAssignTo, setSelectedAssignTo] = useState("");
  const [assignToList, setAssignToList] = useState([defaultAssignTo]);
  const [assignToLoading, setAssignToLoading] = useState(true);
  const [isRetest, setIsRetest] = useState(false);
  const [selectedOverdueStatus, setSelectedOverdueStatus] = useState("");
  // const [selectedAISoftwareQCDiscrepancy, setSelectedAISoftwareQCDiscrepancy] = useState(false);

  const [isWoDetailsModalVisible, setIsWoDetailsModalVisible] = useState(false);
  const [woDetailsModalWorkorderPk, setWoDetailsModalWorkorderPk] = useState(null);
  const [woDetailsModalGenistaId, setWoDetailsModalGenistaId] = useState(null);

  const defaultPageSize = 100;
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: defaultPageSize,
    pageSizeOptions: ["10", "50", "100", "200"],
  });
  const isMounted = useRef(true); // Track mounted state
  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  const { setNewAbortController } = useAbortController();
  const isMountedRef = useRef(true);

  /** Fetch test categories */
  const fetchTestCategories = async () => {
    const { success, results, message } = await fetchTestCategory();
    if (!isMounted.current) return;
    if (success) {
      const newCategoryList = [defaultCategoryOption];
      results.forEach((item) => {
        const { test_category_name } = item;
        newCategoryList.push({ value: test_category_name, label: test_category_name });
      });
      setTestCategories(newCategoryList);
    } else {
      toast.error(message);
    }
  };

  /** Fetch the test qc data */
  const fetchQcApi = async (resetPage = false) => {
    if (!isMountedRef.current) {
      return {
        success: false,
        canceled: true,
        result: [],
        message: "",
      };
    }
    setLoading(true);
    try {
      const newAbortController = setNewAbortController();

      // Common fields for /manualqc/ API
      const basePayload = {
        search,
        date_range: formatAntdDateRange(dateRange),
        test_category: selectedCategory,
        customer_id: selectedCustomer,
        manual_qc_assigned_to: selectedAssignTo,
        is_retest: isRetest ? "true" : "",
        user_id: getUser()?.id,
      };

      let payload = {};
      if (isHistory) {
        payload = {
          ...basePayload,
          ...(selectedManualQC !== "" ? { manual_qc_status: selectedManualQC } : {}),
          source,
        };
      } else {
        payload = {
          ...basePayload,
          manual_qc_status: selectedManualQC,
          due_by: selectedOverdueStatus,
        };
      }
      const {
        success, result, message, canceled,
      } = await fetchQCData(payload, newAbortController.signal);
      const resultArray = Array.isArray(result) ? result : [];
      if (!isMounted.current) {
        return {
          success: false,
          canceled: true,
          result: [],
          message: "",
        };
      }

      if (success) {
        const newData = resultArray.map((item) => ({
          ...normalizeManualQcItem(item),
          key: uuidv4(),
        }));
        setData(newData);
        setPaginationConfig((prev) => {
          const newConfig = { ...prev };
          newConfig.total = newData.length;
          if (resetPage) {
            newConfig.current = 1;
          }
          return newConfig;
        });
      } else if (!canceled) {
        toast.error(message);
        setData([]);
      }

      if (!isMountedRef.current) {
        return {
          success: false,
          canceled: true,
          result: [],
          message: "",
        };
      }
      return {
        success,
        canceled,
        result: success ? resultArray : [],
        message: message || "",
      };
    } catch (error) {
      if (isMountedRef.current) {
        toast.error("Failed to fetch QC data.");
      }
      return {
        success: false,
        canceled: false,
        result: [],
        message: error?.message || "Failed to fetch QC data.",
      };
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchCustomerList = async () => {
    const response = await getSearchOptions();
    if (!isMounted.current) return;
    const result = [defaultCustomerOption];
    if (response && response.success) {
      response.list.forEach(
        (item) => {
          result.push({
            value: item.customer_id,
            label: `${item.company} (${item.customer_id})`,
          });
        },
      );
      setCustomerList(result);
    } else {
      toast.info(response.message);
    }
    if (!isMountedRef.current) return;
    setCustomerLoading(false);
  };

  const fetchQCAnalystList = async () => {
    const response = await fetchQCAnalystUsers({ role: null });
    if (!isMounted.current) return;
    const result = [defaultAssignTo];
    if (response && response.success) {
      response.results.forEach(
        (item) => {
          const { role } = item;
          if ([15, 22].includes(role)) { // QC Analyst (role 15) and Data Ops Lead (role 22)
            result.push({
              value: item.id,
              label: `${item.first_name} ${item.last_name}`.trim(),
            });
          }
        },
      );
      setAssignToList(result);
    } else {
      toast.info(response.message);
    }
    if (!isMountedRef.current) return;
    setAssignToLoading(false);
  };

  /**
   * This useEffect is used to track the component's unmount event
   */
  useEffect(() => () => {
    isMounted.current = false; // Component is unmounted, set isMounted to false
  }, []); // eslint-disable-line

  useEffect(() => {
    isMountedRef.current = true;
    fetchQCAnalystList();
    fetchTestCategories();
    fetchCustomerList();
    return () => {
      isMountedRef.current = false;
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    fetchQcApi(true);
  }, [search, dateRange, selectedCategory, selectedCustomer, selectedAssignTo, selectedManualQC, isRetest, selectedOverdueStatus]); // eslint-disable-line
  // [search, dateRange, selectedCategory, selectedCustomer, selectedAssignTo, selectedAIQC, selectedSoftwareQC, selectedMLSoftQC, selectedManualQC, selectedAISoftwareQCDiscrepancy]); // eslint-disable-line

  const handlePasteSearchBy = (searchValue) => {
    setSearch(searchValue);
  };

  const handleUpdateAssignTo = async (value, rowData) => {
    const user = getUser();
    if (!isMountedRef.current) return false;
    setLoading(true);
    const { tis_pk, wo_id_pk } = rowData;
    const payload = {
      tis_pk,
      user_id: user.id,
      manual_qc_assigned_to: value,
      wo_id_pk,
    };
    const { success, message } = await markManualQcAssignTo(payload);
    if (!isMounted.current) return false;
    if (success) {
      fetchQcApi();
      return true;
    }
    toast.error(message);
    if (!isMountedRef.current) return false;
    setLoading(false);
    return false;
  };

  return (
    <>
      <div className="QCContainer">
        <QCFilterBar
          setSearch={setSearch}
          dateRange={dateRange}
          setDateRange={setDateRange}
          testCategories={testCategories}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          fetchQcApi={fetchQcApi}
          search={search}
          customerList={customerList}
          selectedCustomer={selectedCustomer}
          setSelectedCustomer={setSelectedCustomer}
          // selectedAIQC={selectedAIQC}
          // setSelectedAIQC={setSelectedAIQC}
          // selectedSoftwareQC={selectedSoftwareQC}
          // setSelectedSoftwareQC={setSelectedSoftwareQC}
          // selectedMLSoftQC={selectedMLSoftQC}
          // setSelectedMLSoftQC={setSelectedMLSoftQC}
          selectedManualQC={selectedManualQC}
          setSelectedManualQC={setSelectedManualQC}
          assignToList={assignToList}
          selectedAssignTo={selectedAssignTo}
          setSelectedAssignTo={setSelectedAssignTo}
          assignToLoading={assignToLoading}
          customerLoading={customerLoading}
          selectedOverdueStatus={selectedOverdueStatus}
          setSelectedOverdueStatus={setSelectedOverdueStatus}
          // selectedAISoftwareQCDiscrepancy={selectedAISoftwareQCDiscrepancy}
          // setSelectedAISoftwareQCDiscrepancy={setSelectedAISoftwareQCDiscrepancy}
          handleRetestOnChange={(value) => setIsRetest(value)}
          manualQcDefaultValue={manualQcDefaultValue}
        />
        <Table
          sticky
          className="QCTable"
          dataSource={data}
          scroll={{ y: "60vh" }}
          pagination={{ ...paginationConfig, onChange: handlePageChange }}
          loading={loading}
          bordered
          components={{
            body: {
              row: QCTableRow,
              cell: QCTableCell,
            },
          }}
          onRow={(record) => ({
            record,
            fetchQcApi: readOnly ? undefined : fetchQcApi,
            readOnly,
            onRowClick,
          })}
          columns={columns.map((col) => ({
            ...col,
            onCell: (record) => ({
              record,
              dataIndex: col.dataIndex,
              handlePasteSearchBy,
              assignToList,
              assignToLoading,
              handleUpdateAssignTo,
              openWoDetailsModal: (workorderPk, genistaId = null) => {
                setWoDetailsModalWorkorderPk(workorderPk);
                setWoDetailsModalGenistaId(genistaId);
                setIsWoDetailsModalVisible(true);
              },
              readOnly,
              isHistory,
            }),
          }))}
        />
      </div>
      {isWoDetailsModalVisible && (
        <WoDetailsModal
          workorderPk={woDetailsModalWorkorderPk}
          genistaId={woDetailsModalGenistaId}
          closeModal={() => {
            setIsWoDetailsModalVisible(false);
            setWoDetailsModalWorkorderPk(null);
            setWoDetailsModalGenistaId(null);
          }}
        />
      )}
    </>
  );
};

export default QC;
